import { HelpCatalogue, HelpLang, HelpModule } from './types';
import { HELP_TRANSLATION_LOADERS, HelpTranslationLoader, applyTranslations } from './i18n';
import { currentHelpLang } from './lang';

export type HelpModuleLoader = () => Promise<{ default: HelpModule }>;

/**
 * One loader per catalogue file. Register a new module here (and nowhere else); the files are
 * code-split so help text costs nothing until help mode is first turned on.
 */
export const HELP_MODULE_LOADERS: Record<string, HelpModuleLoader> = {
    dashboard: () => import('./dashboard'),
    holidays: () => import('./holidays'),
    sales: () => import('./sales'),
    store: () => import('./store'),
    maintenance: () => import('./maintenance'),
    procurement: () => import('./procurement'),
    accounting: () => import('./accounting'),
    banking: () => import('./banking'),
    projects: () => import('./projects'),
    paymentRun: () => import('./paymentRun'),
    commercialReports: () => import('./commercialReports'),
    financing: () => import('./financing'),
    recurringDocs: () => import('./recurringDocs'),
    employeeConsumption: () => import('./employeeConsumption'),
    tax: () => import('./tax'),
    crm: () => import('./crm'),
    fixedAssets: () => import('./fixedAssets'),
    commercial: () => import('./commercial'),
    costing: () => import('./costing'),
    merchandising: () => import('./merchandising'),
    qcCommission: () => import('./qcCommission'),
    people: () => import('./people'),
    landingOps: () => import('./landingOps'),
    landingFinance: () => import('./landingFinance'),
    dashboardTiles: () => import('./dashboardTiles'),
    dashboardWidgets: () => import('./dashboardWidgets'),
    dashboardPages: () => import('./dashboardPages'),
    dashboardRoleBoards: () => import('./dashboardRoleBoards'),
    listsAdmin: () => import('./listsAdmin'),
    listsOps: () => import('./listsOps'),
    listsPayroll1: () => import('./listsPayroll1'),
    listsPayroll2: () => import('./listsPayroll2'),
    listsTime: () => import('./listsTime'),
    listsExpense: () => import('./listsExpense'),
    listsRegisters: () => import('./listsRegisters'),
    listsStore: () => import('./listsStore'),
    listsProcurement: () => import('./listsProcurement'),
    listsAssets: () => import('./listsAssets'),
    listsMaintenance: () => import('./listsMaintenance'),
    listsData: () => import('./listsData'),
    formsFixedAssets: () => import('./formsFixedAssets'),
    formsItem: () => import('./formsItem'),
    itemNature: () => import('./itemNature'),
    stockReservation: () => import('./stockReservation'),
    formsStore: () => import('./formsStore'),
    formsStoreOps: () => import('./formsStoreOps'),
    formsCosting: () => import('./formsCosting'),
    formsCashForecast: () => import('./formsCashForecast'),
    formsRecurring: () => import('./formsRecurring'),
    formsAccountingCore: () => import('./formsAccountingCore'),
    formsTax: () => import('./formsTax'),
    formsAccountingFx: () => import('./formsAccountingFx'),
    formsAccountingBudget: () => import('./formsAccountingBudget'),
    formsAccountingTax: () => import('./formsAccountingTax'),
    formsAccountingCorpTax: () => import('./formsAccountingCorpTax'),
    formsProcurement: () => import('./formsProcurement'),
    formsBankAccounts: () => import('./formsBankAccounts'),
    formsBankRec: () => import('./formsBankRec'),
    formsBankCheques: () => import('./formsBankCheques'),
    formsBankTreasury: () => import('./formsBankTreasury'),
    formsFinancing: () => import('./formsFinancing'),
    formsMaintenanceEquip: () => import('./formsMaintenanceEquip'),
    formsMaintenancePm: () => import('./formsMaintenancePm'),
    formsMaintenanceOps: () => import('./formsMaintenanceOps'),
    formsMaintenanceSettings: () => import('./formsMaintenanceSettings'),
    formsMaintenanceWo: () => import('./formsMaintenanceWo'),
    formsCompliance: () => import('./formsCompliance'),
    formsInsurance: () => import('./formsInsurance'),
    formsPieceRate: () => import('./formsPieceRate'),
    formsNotice: () => import('./formsNotice'),
    formsTask: () => import('./formsTask'),
    formsBilling: () => import('./formsBilling'),
    formsSaas: () => import('./formsSaas'),
    formsProject: () => import('./formsProject'),
    formsCrm: () => import('./formsCrm'),
    formsSalesInvoice: () => import('./formsSalesInvoice'),
    formsSalesDocs: () => import('./formsSalesDocs'),
    formsSalesCredit: () => import('./formsSalesCredit'),
    formsSalesReceipt: () => import('./formsSalesReceipt'),
    formsSalesParty: () => import('./formsSalesParty'),
    formsSalesSettings: () => import('./formsSalesSettings'),
    formsSalesBranding: () => import('./formsSalesBranding'),
    formsSalesGateway: () => import('./formsSalesGateway'),
    formsSalesPortal: () => import('./formsSalesPortal'),
    formsSalesCollections: () => import('./formsSalesCollections'),
    formsSalesDunning: () => import('./formsSalesDunning'),
    formsSalesRemittance: () => import('./formsSalesRemittance'),
    formsSalesMisc: () => import('./formsSalesMisc'),
    formsSalesPayouts: () => import('./formsSalesPayouts'),
    formsSalesFulfil: () => import('./formsSalesFulfil'),
    formsCommercial: () => import('./formsCommercial'),
    formsCommercialInvoice: () => import('./formsCommercialInvoice'),
    formsCommercialShipping: () => import('./formsCommercialShipping'),
    formsCommercialExp: () => import('./formsCommercialExp'),
    formsCommercialMisc: () => import('./formsCommercialMisc'),
    formsCommercialLists: () => import('./formsCommercialLists'),
    formsMerch: () => import('./formsMerch'),
    formsMerchSamples: () => import('./formsMerchSamples'),
    formsHoliday: () => import('./formsHoliday'),
    formsDeviceUser: () => import('./formsDeviceUser'),
    formsApprovals: () => import('./formsApprovals'),
    formsShifts: () => import('./formsShifts'),
    formsExpense: () => import('./formsExpense'),
    formsPayrollSettings: () => import('./formsPayrollSettings'),
    formsPayrollPeople: () => import('./formsPayrollPeople'),
    formsPayrollRuns: () => import('./formsPayrollRuns'),
    formsLeave: () => import('./formsLeave'),
    formsAttendance: () => import('./formsAttendance'),
    formsUsers: () => import('./formsUsers'),
    formsInstitutions: () => import('./formsInstitutions'),
    formsCommon: () => import('./formsCommon'),
    formsRoles: () => import('./formsRoles'),
    formsDataExchange: () => import('./formsDataExchange'),
    formsNotifications: () => import('./formsNotifications'),
    formsAuth: () => import('./formsAuth'),
    formsDepartment: () => import('./formsDepartment'),
    formsDashboardManaged: () => import('./formsDashboardManaged'),
    feedback: () => import('./feedback'),
};

/** Merge module files into one catalogue. A key defined in two modules is a bug, so it throws. */
export function mergeModules(modules: Record<string, HelpModule>): HelpCatalogue {
    const out: HelpCatalogue = { elements: {}, sections: {} };
    const owner: Record<string, string> = {};
    for (const [name, mod] of Object.entries(modules)) {
        for (const kind of ['elements', 'sections'] as const) {
            for (const [key, entry] of Object.entries(mod[kind] ?? {})) {
                const id = `${kind}:${key}`;
                if (owner[id]) throw new Error(`Help key "${key}" is defined in both "${owner[id]}" and "${name}"`);
                owner[id] = name;
                (out[kind] as Record<string, unknown>)[key] = entry;
            }
        }
    }
    return out;
}

export async function loadHelpCatalogue(
    loaders: Record<string, HelpModuleLoader> = HELP_MODULE_LOADERS,
    lang: HelpLang = currentHelpLang(),
    translationLoaders: Partial<Record<HelpLang, Record<string, HelpTranslationLoader>>> = HELP_TRANSLATION_LOADERS,
): Promise<HelpCatalogue> {
    const names = Object.keys(loaders);
    const loaded = await Promise.all(names.map(n => loaders[n]()));
    const modules: Record<string, HelpModule> = {};
    names.forEach((n, i) => { modules[n] = loaded[i].default; });
    const english = mergeModules(modules);
    // English is the source; another language is laid over it and falls back to English per text.
    const forLang = lang === 'en' ? [] : Object.values(translationLoaders[lang] ?? {});
    if (forLang.length === 0) return english;
    const translations = await Promise.all(forLang.map(l => l()));
    const { catalogue, unknownKeys } = applyTranslations(english, translations.map(t => t.default), lang);
    if (unknownKeys.length && process.env.NODE_ENV !== 'production') {
        console.warn(`Help translations (${lang}) name keys that no longer exist: ${unknownKeys.join(', ')}`);
    }
    return catalogue;
}

let cached: Promise<HelpCatalogue> | null = null;

/** Memoised default catalogue (loaded once per page load, on first use). A failed load is retried next time. */
export function loadDefaultCatalogue(): Promise<HelpCatalogue> {
    if (!cached) {
        cached = loadHelpCatalogue().catch(err => { cached = null; throw err; });
    }
    return cached;
}
